import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { axios } from '../libs';
import { User } from '../states/actions';
import logger from '../utils/logger';

const SUBSCRIPTION_PLAN_KEYS = ['monthly', 'monthly30', 'annual', 'annual30', 'daily', 'daily30', 'min5', 'business_monthly', 'business_annual'];
const BILLING_CYCLE_OPTIONS = [
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'annually', label: 'Annually' },
	{ value: 'daily', label: 'Daily' },
	{ value: 'minutes', label: 'Minutes' },
];

const cloneSubscriptionPlans = (plans) => (plans && typeof plans === 'object' ? JSON.parse(JSON.stringify(plans)) : {});

const defaultMsgNotifyFormFields = (data = {}) => ({
	msgNotifyLawyerEnabled: data?.msgNotifyLawyerEnabled !== undefined ? Boolean(data.msgNotifyLawyerEnabled) : true,
	msgNotifyLawyerDelayMin: data?.msgNotifyLawyerDelayMin !== undefined ? Number(data.msgNotifyLawyerDelayMin) : 5,
	msgNotifyClientEnabled: data?.msgNotifyClientEnabled !== undefined ? Boolean(data.msgNotifyClientEnabled) : true,
	msgNotifyClientDelayMin: data?.msgNotifyClientDelayMin !== undefined ? Number(data.msgNotifyClientDelayMin) : 5,
});

/** Parses delay minutes for save; 0 is valid (instant notify), NaN/negative fall back to 5. */
function parseMsgNotifyDelayMin(value, fallback = 5) {
	const n = parseInt(value, 10);
	if (!Number.isFinite(n) || n < 0) {
		return fallback;
	}
	return Math.min(n, 24 * 60);
}

export const Settings = () => {
	const user = useSelector(User.get());
	const [settings, setSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const [formData, setFormData] = useState({});
	const [saving, setSaving] = useState(false);
	const [isRecalculating, setIsRecalculating] = useState(false);
	const [lastRecalculation, setLastRecalculation] = useState(null);
	const [sidebarHtmlLocale, setSidebarHtmlLocale] = useState('et');
	const [sidebarHtmlEditorView, setSidebarHtmlEditorView] = useState('source');
	const sidebarHtmlVisualRef = useRef(null);

	/* Fetch CRM settings */
	useEffect(() => {
		// Only fetch if user is admin
		if (!user || user.role !== 'admin') {
			setLoading(false);
			return;
		}
		const fetchSettings = async () => {
			try {
				setLoading(true);
				const response = await axios.get('/crm/settings');
				const data = response.data || response;
				setSettings(data);
				setFormData({
					spreadsheetId: data.spreadsheetId || '',
					cronEnabled: data.cronEnabled || false,
					cronSchedule: data.cronSchedule || '0 3 * * *',
					subscriptionPlans: cloneSubscriptionPlans(data.subscriptionPlans),
					subscriptionBillingUserNo: data.subscriptionBillingUserNo || '',
					newRequestUrl: data.newRequestUrl || '',
					hugoPlusUrl: data.hugoPlusUrl || '',
					clientSupportUrl: data.clientSupportUrl || '',
					sidebarHtml_et: data.sidebarHtml_et || '',
					sidebarHtml_en: data.sidebarHtml_en || '',
					sidebarHtml_ru: data.sidebarHtml_ru || '',
					widgetTermsUrl_et: data.widgetTermsUrl_et || '',
					widgetTermsUrl_en: data.widgetTermsUrl_en || '',
					widgetTermsUrl_ru: data.widgetTermsUrl_ru || '',
					widgetPrivacyUrl_et: data.widgetPrivacyUrl_et || '',
					widgetPrivacyUrl_en: data.widgetPrivacyUrl_en || '',
					widgetPrivacyUrl_ru: data.widgetPrivacyUrl_ru || '',
					clientPortalUploadMaxFileMb: data.clientPortalUploadMaxFileMb ?? 25,
					clientPortalUploadMaxBatchMb: data.clientPortalUploadMaxBatchMb ?? 100,
					clientPortalUploadBlockedExtensions:
						data.clientPortalUploadBlockedExtensions ||
						'exe, bat, cmd, com, scr, vbs, js, mjs, cjs, sh, ps1, msi, dll, jar, hta, wsf',
					...defaultMsgNotifyFormFields(data),
				});
				// Fetch last recalculation info
				fetchLastRecalculation();
			} catch (error) {
				logger.error('Error fetching CRM settings:', error);
				setError('Failed to load settings');
			} finally {
				setLoading(false);
			}
		};
		
		fetchSettings();
	}, [user]);

	useLayoutEffect(() => {
		if (!isEditing || sidebarHtmlEditorView !== 'visual') {
			return undefined;
		}
		const el = sidebarHtmlVisualRef.current;
		if (!el) {
			return undefined;
		}
		if (document.activeElement === el) {
			return undefined;
		}
		const key = `sidebarHtml_${sidebarHtmlLocale}`;
		const html = formData[key] ?? '';
		if (el.innerHTML !== html) {
			el.innerHTML = html;
		}
		return undefined;
	}, [isEditing, sidebarHtmlEditorView, sidebarHtmlLocale, formData.sidebarHtml_et, formData.sidebarHtml_en, formData.sidebarHtml_ru]);

	// Check if user is admin - after all hooks
	if (!user || user.role !== 'admin') {
		return (
			<div id="settings" className="row table_v2">
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="x_content">
							<div style={{ textAlign: 'center', padding: '50px' }}>
								<p className="text-danger">Access denied. Admin only.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	/* Handle form input changes */
	const handleInputChange = (field, value) => {
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
	};

	/* Handle cron toggle */
	const handleCronToggle = async () => {
		if (saving) return;
		
		const currentValue = isEditing ? formData.cronEnabled : settings?.cronEnabled;
		const newValue = !currentValue;
		const currentSchedule = isEditing ? formData.cronSchedule : settings?.cronSchedule;
		
		try {
			setSaving(true);
			const response = await axios.put('/crm/settings', {
				spreadsheetId: (isEditing ? formData.spreadsheetId : settings?.spreadsheetId) || '',
				cronEnabled: newValue,
				cronSchedule: currentSchedule || '0 3 * * *',
				subscriptionPlans: cloneSubscriptionPlans(settings?.subscriptionPlans),
			});
			
			const updatedData = response.data || response;
			setSettings(updatedData);
			setFormData(prev => ({
				...prev,
				cronEnabled: updatedData.cronEnabled || false,
				cronSchedule: updatedData.cronSchedule || '0 3 * * *',
				subscriptionPlans: cloneSubscriptionPlans(updatedData.subscriptionPlans),
				newRequestUrl: updatedData.newRequestUrl || '',
				hugoPlusUrl: updatedData.hugoPlusUrl !== undefined ? (updatedData.hugoPlusUrl || '') : prev.hugoPlusUrl,
				clientSupportUrl:
					updatedData.clientSupportUrl !== undefined ? (updatedData.clientSupportUrl || '') : prev.clientSupportUrl,
				sidebarHtml_et: updatedData.sidebarHtml_et !== undefined ? (updatedData.sidebarHtml_et || '') : prev.sidebarHtml_et,
				sidebarHtml_en: updatedData.sidebarHtml_en !== undefined ? (updatedData.sidebarHtml_en || '') : prev.sidebarHtml_en,
				sidebarHtml_ru: updatedData.sidebarHtml_ru !== undefined ? (updatedData.sidebarHtml_ru || '') : prev.sidebarHtml_ru,
				widgetTermsUrl_et: updatedData.widgetTermsUrl_et !== undefined ? (updatedData.widgetTermsUrl_et || '') : prev.widgetTermsUrl_et,
				widgetTermsUrl_en: updatedData.widgetTermsUrl_en !== undefined ? (updatedData.widgetTermsUrl_en || '') : prev.widgetTermsUrl_en,
				widgetTermsUrl_ru: updatedData.widgetTermsUrl_ru !== undefined ? (updatedData.widgetTermsUrl_ru || '') : prev.widgetTermsUrl_ru,
				widgetPrivacyUrl_et: updatedData.widgetPrivacyUrl_et !== undefined ? (updatedData.widgetPrivacyUrl_et || '') : prev.widgetPrivacyUrl_et,
				widgetPrivacyUrl_en: updatedData.widgetPrivacyUrl_en !== undefined ? (updatedData.widgetPrivacyUrl_en || '') : prev.widgetPrivacyUrl_en,
				widgetPrivacyUrl_ru: updatedData.widgetPrivacyUrl_ru !== undefined ? (updatedData.widgetPrivacyUrl_ru || '') : prev.widgetPrivacyUrl_ru,
			}));
			
			window.nSuccess({
				title: 'Success!',
				text: `Automatic sync ${newValue ? 'enabled' : 'disabled'} successfully`,
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error updating cron settings:', error);
			window.nError({
				title: 'Error!',
				text: error.response?.data?.message || error.message || 'Failed to update cron settings',
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSaving(false);
		}
	};

	/* Handle save */
	const handleSave = async () => {
		let sidebarEt = formData.sidebarHtml_et || '';
		let sidebarEn = formData.sidebarHtml_en || '';
		let sidebarRu = formData.sidebarHtml_ru || '';
		if (isEditing && sidebarHtmlEditorView === 'visual' && sidebarHtmlVisualRef.current) {
			const html = sidebarHtmlVisualRef.current.innerHTML;
			if (sidebarHtmlLocale === 'et') sidebarEt = html;
			else if (sidebarHtmlLocale === 'en') sidebarEn = html;
			else sidebarRu = html;
		}
		try {
			setSaving(true);
			const response = await axios.put('/crm/settings', {
				spreadsheetId: formData.spreadsheetId || '',
				cronEnabled: formData.cronEnabled || false,
				cronSchedule: formData.cronSchedule || '0 3 * * *',
				subscriptionPlans: cloneSubscriptionPlans(formData.subscriptionPlans),
				subscriptionBillingUserNo: formData.subscriptionBillingUserNo || '',
				newRequestUrl: formData.newRequestUrl || '',
				hugoPlusUrl: formData.hugoPlusUrl || '',
				clientSupportUrl: formData.clientSupportUrl || '',
				sidebarHtml_et: sidebarEt,
				sidebarHtml_en: sidebarEn,
				sidebarHtml_ru: sidebarRu,
				widgetTermsUrl_et: formData.widgetTermsUrl_et || '',
				widgetTermsUrl_en: formData.widgetTermsUrl_en || '',
				widgetTermsUrl_ru: formData.widgetTermsUrl_ru || '',
				widgetPrivacyUrl_et: formData.widgetPrivacyUrl_et || '',
				widgetPrivacyUrl_en: formData.widgetPrivacyUrl_en || '',
				widgetPrivacyUrl_ru: formData.widgetPrivacyUrl_ru || '',
				msgNotifyLawyerEnabled: formData.msgNotifyLawyerEnabled !== false,
				msgNotifyLawyerDelayMin: parseMsgNotifyDelayMin(formData.msgNotifyLawyerDelayMin),
				msgNotifyClientEnabled: formData.msgNotifyClientEnabled !== false,
				msgNotifyClientDelayMin: parseMsgNotifyDelayMin(formData.msgNotifyClientDelayMin),
				clientPortalUploadMaxFileMb: formData.clientPortalUploadMaxFileMb,
				clientPortalUploadMaxBatchMb: formData.clientPortalUploadMaxBatchMb,
				clientPortalUploadBlockedExtensions: formData.clientPortalUploadBlockedExtensions || '',
			});
			
			const updatedData = response.data || response;
			if (updatedData?.status && updatedData.status >= 400) {
				window.nError({
					title: 'Error!',
					text: updatedData.message || 'Failed to save settings',
					icon: 'fa-exclamation-triangle',
				});
				return;
			}
			setSettings(updatedData);
			setFormData({
				spreadsheetId: updatedData.spreadsheetId || '',
				cronEnabled: updatedData.cronEnabled || false,
				cronSchedule: updatedData.cronSchedule || '0 3 * * *',
				subscriptionPlans: cloneSubscriptionPlans(updatedData.subscriptionPlans),
				newRequestUrl: updatedData.newRequestUrl || '',
				hugoPlusUrl: updatedData.hugoPlusUrl || '',
				clientSupportUrl: updatedData.clientSupportUrl || '',
				sidebarHtml_et: updatedData.sidebarHtml_et || '',
				sidebarHtml_en: updatedData.sidebarHtml_en || '',
				sidebarHtml_ru: updatedData.sidebarHtml_ru || '',
				widgetTermsUrl_et: updatedData.widgetTermsUrl_et || '',
				widgetTermsUrl_en: updatedData.widgetTermsUrl_en || '',
				widgetTermsUrl_ru: updatedData.widgetTermsUrl_ru || '',
				widgetPrivacyUrl_et: updatedData.widgetPrivacyUrl_et || '',
				widgetPrivacyUrl_en: updatedData.widgetPrivacyUrl_en || '',
				widgetPrivacyUrl_ru: updatedData.widgetPrivacyUrl_ru || '',
				clientPortalUploadMaxFileMb: updatedData.clientPortalUploadMaxFileMb ?? 25,
				clientPortalUploadMaxBatchMb: updatedData.clientPortalUploadMaxBatchMb ?? 100,
				clientPortalUploadBlockedExtensions:
					updatedData.clientPortalUploadBlockedExtensions ||
					formData.clientPortalUploadBlockedExtensions ||
					'',
				...defaultMsgNotifyFormFields(updatedData),
			});
			setIsEditing(false);
			
			window.nSuccess({
				title: 'Success!',
				text: 'Settings saved successfully',
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error saving settings:', error);
			window.nError({
				title: 'Error!',
				text: error.response?.data?.message || error.message || 'Failed to save settings',
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setSaving(false);
		}
	};

	/* Handle cancel */
	const handleCancel = () => {
		setFormData({
			spreadsheetId: settings?.spreadsheetId || '',
			cronEnabled: settings?.cronEnabled || false,
			cronSchedule: settings?.cronSchedule || '0 3 * * *',
			subscriptionPlans: cloneSubscriptionPlans(settings?.subscriptionPlans),
			subscriptionBillingUserNo: settings?.subscriptionBillingUserNo || '',
			newRequestUrl: settings?.newRequestUrl || '',
			hugoPlusUrl: settings?.hugoPlusUrl || '',
			clientSupportUrl: settings?.clientSupportUrl || '',
			sidebarHtml_et: settings?.sidebarHtml_et || '',
			sidebarHtml_en: settings?.sidebarHtml_en || '',
			sidebarHtml_ru: settings?.sidebarHtml_ru || '',
			widgetTermsUrl_et: settings?.widgetTermsUrl_et || '',
			widgetTermsUrl_en: settings?.widgetTermsUrl_en || '',
			widgetTermsUrl_ru: settings?.widgetTermsUrl_ru || '',
			widgetPrivacyUrl_et: settings?.widgetPrivacyUrl_et || '',
			widgetPrivacyUrl_en: settings?.widgetPrivacyUrl_en || '',
			widgetPrivacyUrl_ru: settings?.widgetPrivacyUrl_ru || '',
			clientPortalUploadMaxFileMb: settings?.clientPortalUploadMaxFileMb ?? 25,
			clientPortalUploadMaxBatchMb: settings?.clientPortalUploadMaxBatchMb ?? 100,
			clientPortalUploadBlockedExtensions:
				settings?.clientPortalUploadBlockedExtensions ||
				'exe, bat, cmd, com, scr, vbs, js, mjs, cjs, sh, ps1, msi, dll, jar, hta, wsf',
			...defaultMsgNotifyFormFields(settings),
		});
		setIsEditing(false);
	};

	const handlePlanFieldChange = (planKey, field, value) => {
		setFormData(prev => {
			const plans = cloneSubscriptionPlans(prev.subscriptionPlans);
			if (!plans[planKey]) return prev;
			if (field === 'price') {
				const n = parseFloat(String(value).replace(',', '.'));
				plans[planKey] = { ...plans[planKey], price: Number.isFinite(n) ? n : plans[planKey].price };
			} else {
				plans[planKey] = { ...plans[planKey], [field]: value };
			}
			return { ...prev, subscriptionPlans: plans };
		});
	};

	/* Handle open spreadsheet */
	const handleOpenSpreadsheet = () => {
		const spreadsheetId = isEditing ? formData.spreadsheetId : settings?.spreadsheetId;
		if (spreadsheetId) {
			window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`, '_blank');
		} else {
			window.nError({
				title: 'Error!',
				text: 'No spreadsheet ID configured',
				icon: 'fa-exclamation-triangle'
			});
		}
	};

	/* Handle recalculate financials */
	const handleRecalculateFinancials = async () => {
		if (!window.confirm('Recalculate billed, unbilled, deadline, retainer and balance for all projects. This may take a few minutes. Continue?')) {
			return;
		}
		
		setIsRecalculating(true);
		try {
			const response = await axios.post('/projects/recalculate-financials');
			logger.log('Recalculation response:', response);
			
			// Handle both possible response formats
			const data = response.data || response;
			const successCount = data.success || 0;
			const durationMs = data.duration_ms || 0;
			const errorCount = data.errors?.length || 0;
			
			window.nSuccess({
				title: 'Success!',
				text: `Recalculation complete! ${successCount} projects updated${errorCount > 0 ? `, ${errorCount} errors` : ''} in ${durationMs}ms`,
				icon: 'fa-check-square'
			});
			fetchLastRecalculation();
		} catch (error) {
			logger.error('Error recalculating financials:', error);
			window.nError({
				title: 'Error!',
				text: error.response?.data?.error || error.message || 'Failed to recalculate financials',
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setIsRecalculating(false);
		}
	};

	/* Fetch last recalculation info */
	const fetchLastRecalculation = async () => {
		try {
			const response = await axios.get('/projects/recalculate-financials/last');
			setLastRecalculation(response.data);
		} catch (error) {
			logger.error('Error fetching last recalculation info:', error);
		}
	};

	if (loading) {
		return (
			<div id="settings" className="row table_v2">
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="x_content">
							<div style={{ textAlign: 'center', padding: '50px' }}>
								<p>Loading settings...</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	
	if (error) {
		return (
			<div id="settings" className="row table_v2">
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="x_content">
							<div style={{ textAlign: 'center', padding: '50px' }}>
								<p className="text-danger">{error}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div id="settings" className="row table_v2">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>Settings</h1>
						</div>
						<div className="header-actions d-flex align-items-center">
							{!isEditing ? (
								<div className="action action-addnew">
									<button className="button button-blue" onClick={() => setIsEditing(true)}>
										Edit Settings
									</button>
								</div>
							) : (
								<div className="action-group d-flex gap-2">
									<button className="button" onClick={handleCancel} disabled={saving}>
										Cancel
									</button>
								</div>
							)}
						</div>
					</div>
					<div className="x_content">
						<div className="modal-form" style={{paddingTop: '20px', maxWidth: '800px', margin: '0 auto'}}>
							{/* CRM Data Spreadsheet Section */}
							<div className="form-row col-full row-label" style={{marginTop: '10px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>CRM Data Spreadsheet</h3>
							</div>
							
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Spreadsheet ID
											</label>
											<div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
												<input
													type="text"
													placeholder="Enter Google Spreadsheet ID"
													value={formData.spreadsheetId || ''}
													onChange={e => handleInputChange('spreadsheetId', e.target.value)}
													style={{ 
														flex: 1,
														padding: '8px 12px',
														border: '1px solid #ddd',
														borderRadius: '4px',
														fontSize: '14px'
													}}
												/>
												<button
													type="button"
													className="button"
													onClick={handleOpenSpreadsheet}
													disabled={!formData.spreadsheetId}
													style={{ whiteSpace: 'nowrap' }}
												>
													Open Spreadsheet
												</button>
											</div>
											<div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
												Leave empty to clear the spreadsheet ID setting
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Spreadsheet ID</label>
											<div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
												<div style={{ fontWeight: '500', flex: 1 }}>
													{settings?.spreadsheetId || '—'}
												</div>
												{settings?.spreadsheetId && (
													<button
														type="button"
														className="button button-blue"
														onClick={handleOpenSpreadsheet}
													>
														Open Spreadsheet
													</button>
												)}
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Cron Job Section */}
							<div className="form-row col-full row-label" style={{marginTop: '30px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>Automatic Sync (Cron Job)</h3>
							</div>
							
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									<div className="action action-switch" style={{marginBottom: '20px'}}>
										<div className="btn-switch" onClick={handleCronToggle} style={{ cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
											<div className={`switch ${(isEditing ? formData.cronEnabled : settings?.cronEnabled) ? 'on' : 'off'}`}>
												<span></span>
											</div>
											<span className="text">Enable Automatic Sync</span>
										</div>
									</div>
									
									{(isEditing ? formData.cronEnabled : settings?.cronEnabled) && (
										<div style={{ marginTop: '20px' }}>
											{isEditing ? (
												<>
													<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
														Cron Schedule
													</label>
													<input
														type="text"
														placeholder="0 3 * * * (daily at 3 AM)"
														value={formData.cronSchedule || ''}
														onChange={e => handleInputChange('cronSchedule', e.target.value)}
														style={{ 
															width: '100%',
															padding: '8px 12px',
															border: '1px solid #ddd',
															borderRadius: '4px',
															fontSize: '14px'
														}}
													/>
													<div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
														Cron expression format: minute hour day month weekday (e.g., "0 3 * * *" for daily at 3 AM)
													</div>
												</>
											) : (
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Schedule</label>
													<div style={{ fontWeight: '500' }}>
														{settings?.cronSchedule || '0 3 * * *'}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>

							{/* Subscription plans (EveryPay / LHV) */}
							<div className="form-row col-full row-label" style={{marginTop: '30px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>Subscription plans</h3>
								<p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
									Prices and labels used for subscription checkout and renewals. Plan keys are fixed so existing chat flows keep working.
								</p>
							</div>
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px', overflowX: 'auto'}}>
									<table className="table table-bordered" style={{ fontSize: '13px', marginBottom: 0, minWidth: '520px' }}>
										<thead>
											<tr style={{ background: '#f5f5f5' }}>
												<th>Key</th>
												<th>Name</th>
												<th>Price (EUR)</th>
												<th>Billing cycle</th>
											</tr>
										</thead>
										<tbody>
											{SUBSCRIPTION_PLAN_KEYS.map((key) => {
												const row = (isEditing ? formData.subscriptionPlans : settings?.subscriptionPlans)?.[key];
												if (!row) return null;
												return (
													<tr key={key}>
														<td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{key}</td>
														<td>
															{isEditing ? (
																<input
																	type="text"
																	value={row.name || ''}
																	onChange={e => handlePlanFieldChange(key, 'name', e.target.value)}
																	style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
																/>
															) : (
																row.name
															)}
														</td>
														<td style={{ width: '120px' }}>
															{isEditing ? (
																<input
																	type="number"
																	step="0.01"
																	min="0"
																	value={row.price ?? ''}
																	onChange={e => handlePlanFieldChange(key, 'price', e.target.value)}
																	style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
																/>
															) : (
																Number(row.price).toFixed(2)
															)}
														</td>
														<td style={{ width: '140px' }}>
															{isEditing ? (
																<select
																	value={row.billingCycle || 'monthly'}
																	onChange={e => handlePlanFieldChange(key, 'billingCycle', e.target.value)}
																	style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px' }}
																>
																	{BILLING_CYCLE_OPTIONS.map(opt => (
																		<option key={opt.value} value={opt.value}>{opt.label}</option>
																	))}
																</select>
															) : (
																row.billingCycle
															)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px', marginBottom: '20px'}}>
								<div className="form-row col-full" style={{padding: '0 10px'}}>
									<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
										Subscription billing organization (user_no)
									</label>
									<p style={{ margin: '0 0 8px', fontSize: '13px', color: '#666' }}>
										HUGO Legal user whose organization details appear on subscription invoices (H-family). Required for correct renewal/recharge invoicing.
									</p>
									{!settings?.subscriptionBillingUserNo && !formData.subscriptionBillingUserNo && (
										<div style={{ marginBottom: '10px', padding: '10px', background: '#fdecea', border: '1px solid #f5c2c0', borderRadius: '4px', color: '#842029', fontSize: '13px' }}>
											Not configured — new charges may fall back to legacy E invoices until set.
										</div>
									)}
									{isEditing ? (
										<input
											type="text"
											placeholder="e.g. HUGO legal user_no"
											value={formData.subscriptionBillingUserNo || ''}
											onChange={e => handleInputChange('subscriptionBillingUserNo', e.target.value)}
											style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
										/>
									) : (
										<div style={{ fontWeight: '500' }}>{settings?.subscriptionBillingUserNo || '—'}</div>
									)}
								</div>
							</div>

							{/* Client dashboard (widget) */}
							<div className="form-row col-full row-label" style={{marginTop: '40px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>Client dashboard settings</h3>
								<p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
									Single URL for the &quot;+ new request&quot; link in the client portal (all languages). Also used when visitors open the dashboard root (<code>/</code>). Same pattern as terms and privacy links.
								</p>
							</div>
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												New request URL
											</label>
											<input
												type="url"
												placeholder="https://app.hugo.legal/hugoapp?bot=YOUR_BOT_ID"
												value={formData.newRequestUrl || ''}
												onChange={e => handleInputChange('newRequestUrl', e.target.value)}
												style={{
													width: '100%',
													padding: '8px 12px',
													border: '1px solid #ddd',
													borderRadius: '4px',
													fontSize: '14px',
												}}
											/>
											<div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
												Must start with http:// or https://. If empty, the new-request button is hidden and root visitors see the login page.
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>New request URL</label>
											<div style={{ fontWeight: '500', wordBreak: 'break-all' }}>
												{settings?.newRequestUrl?.trim() ? settings.newRequestUrl : '— (not set)'}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
										Client portal — file upload limits
									</label>
									<p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
										Applied when clients send files in the project thread. Enforced on the server; the portal validates before upload.
									</p>
									{isEditing ? (
										<div style={{ display: 'grid', gap: '12px', maxWidth: '520px' }}>
											<div>
												<label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
													Max single file (MB)
												</label>
												<input
													type="number"
													min="1"
													max="500"
													step="1"
													value={formData.clientPortalUploadMaxFileMb ?? 25}
													onChange={(e) =>
														handleInputChange('clientPortalUploadMaxFileMb', e.target.value)
													}
													style={{
														width: '100%',
														padding: '8px 12px',
														border: '1px solid #ddd',
														borderRadius: '4px',
														fontSize: '14px',
													}}
												/>
											</div>
											<div>
												<label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
													Max total per send (MB)
												</label>
												<input
													type="number"
													min="1"
													max="500"
													step="1"
													value={formData.clientPortalUploadMaxBatchMb ?? 100}
													onChange={(e) =>
														handleInputChange('clientPortalUploadMaxBatchMb', e.target.value)
													}
													style={{
														width: '100%',
														padding: '8px 12px',
														border: '1px solid #ddd',
														borderRadius: '4px',
														fontSize: '14px',
													}}
												/>
											</div>
											<div>
												<label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
													Blocked extensions (deny list, comma-separated)
												</label>
												<textarea
													rows={3}
													value={formData.clientPortalUploadBlockedExtensions || ''}
													onChange={(e) =>
														handleInputChange(
															'clientPortalUploadBlockedExtensions',
															e.target.value,
														)
													}
													style={{
														width: '100%',
														padding: '8px 12px',
														border: '1px solid #ddd',
														borderRadius: '4px',
														fontSize: '14px',
														fontFamily: 'inherit',
													}}
												/>
											</div>
										</div>
									) : (
										<div style={{ fontSize: '13px', lineHeight: 1.6 }}>
											<div>
												<span style={{ color: '#999' }}>Max single file: </span>
												<strong>{settings?.clientPortalUploadMaxFileMb ?? 25} MB</strong>
											</div>
											<div>
												<span style={{ color: '#999' }}>Max per send: </span>
												<strong>{settings?.clientPortalUploadMaxBatchMb ?? 100} MB</strong>
											</div>
											<div style={{ marginTop: '8px' }}>
												<span style={{ color: '#999', display: 'block', marginBottom: '4px' }}>
													Blocked extensions
												</span>
												<div style={{ wordBreak: 'break-word' }}>
													{settings?.clientPortalUploadBlockedExtensions?.trim() ||
														'exe, bat, cmd, … (defaults)'}
												</div>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
										Login footer — terms &amp; privacy (widget)
									</label>
									<p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
										Optional links in the client portal login footer, per language. Leave empty to hide that link. Same URL rules as new request URL (http/https).
									</p>
									{['et', 'en', 'ru'].map((loc) => (
										<div key={loc} style={{ marginBottom: '18px' }}>
											<div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}>{loc.toUpperCase()}</div>
											{isEditing ? (
												<>
													<label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Terms URL</label>
													<input
														type="url"
														placeholder="https://…"
														value={formData[`widgetTermsUrl_${loc}`] || ''}
														onChange={(e) => handleInputChange(`widgetTermsUrl_${loc}`, e.target.value)}
														style={{
															width: '100%',
															padding: '8px 12px',
															border: '1px solid #ddd',
															borderRadius: '4px',
															fontSize: '14px',
															marginBottom: '10px',
														}}
													/>
													<label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Privacy URL</label>
													<input
														type="url"
														placeholder="https://…"
														value={formData[`widgetPrivacyUrl_${loc}`] || ''}
														onChange={(e) => handleInputChange(`widgetPrivacyUrl_${loc}`, e.target.value)}
														style={{
															width: '100%',
															padding: '8px 12px',
															border: '1px solid #ddd',
															borderRadius: '4px',
															fontSize: '14px',
														}}
													/>
												</>
											) : (
												<div style={{ fontSize: '13px' }}>
													<div style={{ marginBottom: '6px' }}>
														<span style={{ color: '#999' }}>Terms: </span>
														<span style={{ fontWeight: '500', wordBreak: 'break-all' }}>
															{settings?.[`widgetTermsUrl_${loc}`]?.trim() ? settings[`widgetTermsUrl_${loc}`] : '—'}
														</span>
													</div>
													<div>
														<span style={{ color: '#999' }}>Privacy: </span>
														<span style={{ fontWeight: '500', wordBreak: 'break-all' }}>
															{settings?.[`widgetPrivacyUrl_${loc}`]?.trim() ? settings[`widgetPrivacyUrl_${loc}`] : '—'}
														</span>
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												HUGO+ button URL (promo sidebar)
											</label>
											<input
												type="text"
												placeholder="https://"
												value={formData.hugoPlusUrl || ''}
												onChange={e => handleInputChange('hugoPlusUrl', e.target.value)}
												style={{
													boxSizing: 'border-box',
													width: '100%',
													minWidth: 0,
													padding: '8px 12px',
													border: '1px solid #ddd',
													borderRadius: '4px',
													fontSize: '14px',
												}}
											/>
											<div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
												Must start with http:// or https://. If empty, the HUGO+ card shows membership text only (no button).
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>HUGO+ button URL</label>
											<div style={{ fontWeight: '500', wordBreak: 'break-all' }}>
												{settings?.hugoPlusUrl?.trim() ? settings.hugoPlusUrl : '— (not set)'}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									{isEditing ? (
										<>
											<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
												Client support link (logged-in sidebar)
											</label>
											<input
												type="text"
												placeholder="https://"
												value={formData.clientSupportUrl || ''}
												onChange={e => handleInputChange('clientSupportUrl', e.target.value)}
												style={{
													boxSizing: 'border-box',
													width: '100%',
													minWidth: 0,
													padding: '8px 12px',
													border: '1px solid #ddd',
													borderRadius: '4px',
													fontSize: '14px',
												}}
											/>
											<div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
												Must start with http:// or https://. If empty, the support row is hidden in the logged-in client portal sidebar.
											</div>
										</>
									) : (
										<div style={{fontSize: '13px', textAlign: 'left'}}>
											<label style={{color: '#999', display: 'block', marginBottom: '4px'}}>Client support link</label>
											<div style={{ fontWeight: '500', wordBreak: 'break-all' }}>
												{settings?.clientSupportUrl?.trim() ? settings.clientSupportUrl : '— (not set)'}
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
										Logged-out sidebar custom HTML
									</label>
									<p style={{ margin: '0 0 12px', fontSize: '13px', color: '#666' }}>
										When non-empty for a language, this replaces the default chatbot description area in the widget sidebar for anonymous users. Use Source for raw HTML or Visual for a simple WYSIWYG (same pattern as chatbot Text block in HTML mode).
									</p>
									<div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
										{['et', 'en', 'ru'].map((loc) => (
											<button
												key={loc}
												type="button"
												className="button button-small"
												onClick={() => {
													if (isEditing && sidebarHtmlEditorView === 'visual' && sidebarHtmlVisualRef.current) {
														const h = sidebarHtmlVisualRef.current.innerHTML;
														setFormData((prev) => ({ ...prev, [`sidebarHtml_${sidebarHtmlLocale}`]: h }));
													}
													setSidebarHtmlLocale(loc);
												}}
												style={{
													fontWeight: sidebarHtmlLocale === loc ? 700 : 400,
													border: sidebarHtmlLocale === loc ? '2px solid #032097' : '1px solid #ccc',
													background: sidebarHtmlLocale === loc ? '#e8eefc' : '#fff',
												}}
											>
												{loc.toUpperCase()}
											</button>
										))}
									</div>
									{isEditing ? (
										<>
											<div className="mode-selector" style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
												{['source', 'visual'].map((view) => (
													<span
														key={view}
														role="button"
														tabIndex={0}
														onKeyDown={(e) => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																e.currentTarget.click();
															}
														}}
														className={`mode-pill ${sidebarHtmlEditorView === view ? 'active' : ''}`}
														style={{
															padding: '6px 12px',
															borderRadius: '4px',
															border: '1px solid #ccc',
															cursor: 'pointer',
															backgroundColor: sidebarHtmlEditorView === view ? '#032097' : '#f0f0f0',
															color: sidebarHtmlEditorView === view ? 'white' : '#000',
															fontSize: '12px',
															fontWeight: 'bold',
															textTransform: 'capitalize',
														}}
														onClick={() => {
															if (view === 'source' && sidebarHtmlEditorView === 'visual' && sidebarHtmlVisualRef.current) {
																const h = sidebarHtmlVisualRef.current.innerHTML;
																setFormData((prev) => ({ ...prev, [`sidebarHtml_${sidebarHtmlLocale}`]: h }));
															}
															setSidebarHtmlEditorView(view);
														}}
													>
														{view}
													</span>
												))}
											</div>
											{sidebarHtmlEditorView === 'source' ? (
												<textarea
													className="form-control"
													rows={10}
													placeholder="HTML content"
													value={formData[`sidebarHtml_${sidebarHtmlLocale}`] ?? ''}
													onChange={(e) => handleInputChange(`sidebarHtml_${sidebarHtmlLocale}`, e.target.value)}
													style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
												/>
											) : (
												<div
													ref={sidebarHtmlVisualRef}
													className="form-control"
													contentEditable
													suppressContentEditableWarning
													onBlur={(e) => handleInputChange(`sidebarHtml_${sidebarHtmlLocale}`, e.currentTarget.innerHTML)}
													style={{
														minHeight: '160px',
														overflow: 'auto',
														textAlign: 'left',
														width: '100%',
													}}
												/>
											)}
										</>
									) : (
										settings?.[`sidebarHtml_${sidebarHtmlLocale}`]?.trim() ? (
											<div
												style={{
													border: '1px solid #eee',
													borderRadius: '4px',
													minHeight: '80px',
													padding: '12px',
													background: '#fafafa',
													fontSize: '13px',
												}}
												dangerouslySetInnerHTML={{
													__html: settings[`sidebarHtml_${sidebarHtmlLocale}`],
												}}
											/>
										) : (
											<div
												style={{
													border: '1px solid #eee',
													borderRadius: '4px',
													minHeight: '80px',
													padding: '12px',
													background: '#fafafa',
													color: '#999',
													fontSize: '13px',
												}}
											>
												(empty)
											</div>
										)
									)}
								</div>
							</div>

							{/* Message notifications */}
							<div className="form-row col-full row-label" style={{marginTop: '30px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>Message notifications</h3>
								<p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
									Email digests when the other party posts in the project communication thread. One email per burst of messages after the delay (not per message).
								</p>
							</div>
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '24px'}}>
									<div className="action action-switch" style={{marginBottom: '12px'}}>
										<div
											className="btn-switch"
											onClick={isEditing ? () => handleInputChange('msgNotifyLawyerEnabled', !(formData.msgNotifyLawyerEnabled !== false)) : undefined}
											style={{ cursor: isEditing ? 'pointer' : 'default' }}
										>
											<div className={`switch ${(isEditing ? formData.msgNotifyLawyerEnabled !== false : settings?.msgNotifyLawyerEnabled !== false) ? 'on' : 'off'}`}>
												<span></span>
											</div>
											<span className="text">Notify lawyer about new client messages</span>
										</div>
									</div>
									{(isEditing ? formData.msgNotifyLawyerEnabled !== false : settings?.msgNotifyLawyerEnabled !== false) && (
										isEditing ? (
											<div style={{ marginLeft: '4px' }}>
												<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
													Delay (minutes)
												</label>
												<input
													type="number"
													min="0"
													step="1"
													value={formData.msgNotifyLawyerDelayMin ?? 5}
													onChange={e => handleInputChange('msgNotifyLawyerDelayMin', parseInt(e.target.value, 10) || 0)}
													style={{ width: '120px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
												/>
											</div>
										) : (
											<div style={{ fontSize: '13px', color: '#666' }}>
												Delay: <strong>{settings?.msgNotifyLawyerDelayMin ?? 5}</strong> minutes
											</div>
										)
									)}
								</div>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '24px'}}>
									<div className="action action-switch" style={{marginBottom: '12px'}}>
										<div
											className="btn-switch"
											onClick={isEditing ? () => handleInputChange('msgNotifyClientEnabled', !(formData.msgNotifyClientEnabled !== false)) : undefined}
											style={{ cursor: isEditing ? 'pointer' : 'default' }}
										>
											<div className={`switch ${(isEditing ? formData.msgNotifyClientEnabled !== false : settings?.msgNotifyClientEnabled !== false) ? 'on' : 'off'}`}>
												<span></span>
											</div>
											<span className="text">Notify client about new lawyer messages</span>
										</div>
									</div>
									{(isEditing ? formData.msgNotifyClientEnabled !== false : settings?.msgNotifyClientEnabled !== false) && (
										isEditing ? (
											<div style={{ marginLeft: '4px' }}>
												<label style={{ marginBottom: '6px', fontWeight: '500', display: 'block', fontSize: '13px', color: '#666' }}>
													Delay (minutes)
												</label>
												<input
													type="number"
													min="0"
													step="1"
													value={formData.msgNotifyClientDelayMin ?? 5}
													onChange={e => handleInputChange('msgNotifyClientDelayMin', parseInt(e.target.value, 10) || 0)}
													style={{ width: '120px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
												/>
											</div>
										) : (
											<div style={{ fontSize: '13px', color: '#666' }}>
												Delay: <strong>{settings?.msgNotifyClientDelayMin ?? 5}</strong> minutes
											</div>
										)
									)}
								</div>
							</div>

							{settings?.msgNotifyEmailPreview && (
								<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px', marginBottom: '24px'}}>
									<div className="form-row col-full" style={{padding: '0 10px'}}>
										<h4 style={{ fontSize: '15px', color: '#333', margin: '0 0 12px' }}>Email templates (read-only preview)</h4>
										<p style={{ fontSize: '12px', color: '#888', margin: '0 0 16px' }}>
											Links use the current server environment. Lawyer → CRM project page; client → widget{' '}
											<code>/project/…</code> (not payment <code>/hugoapp</code>). Override widget host with{' '}
											<code>CLIENT_WIDGET_ORIGIN</code> if needed.
										</p>
										<div style={{ marginBottom: '20px', padding: '12px', background: '#fafafa', border: '1px solid #eee', borderRadius: '4px' }}>
											<div style={{ fontWeight: 600, marginBottom: '8px' }}>Lawyer (Estonian)</div>
											<div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Subject:</div>
											<pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{settings.msgNotifyEmailPreview.lawyer?.subject}</pre>
											<div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Body:</div>
											<div
												style={{ fontSize: '13px', border: '1px solid #eee', padding: '10px', background: '#fff', borderRadius: '4px' }}
												dangerouslySetInnerHTML={{ __html: settings.msgNotifyEmailPreview.lawyer?.body }}
											/>
											<div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
												Link: {settings.msgNotifyEmailPreview.lawyer?.linkExample}
											</div>
										</div>
										{['et', 'en', 'ru'].map((loc) => {
											const block = settings.msgNotifyEmailPreview.client?.[loc];
											if (!block) return null;
											return (
												<div
													key={loc}
													style={{ marginBottom: '16px', padding: '12px', background: '#fafafa', border: '1px solid #eee', borderRadius: '4px' }}
												>
													<div style={{ fontWeight: 600, marginBottom: '8px' }}>Client ({loc.toUpperCase()})</div>
													<div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Subject:</div>
													<pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{block.subject}</pre>
													<div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Body:</div>
													<div
														style={{ fontSize: '13px', border: '1px solid #eee', padding: '10px', background: '#fff', borderRadius: '4px' }}
														dangerouslySetInnerHTML={{ __html: block.body }}
													/>
													<div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
														Link: {block.linkExample}
													</div>
												</div>
											);
										})}
									</div>
								</div>
							)}

							{/* Save button at bottom in edit mode */}
							{isEditing && (
								<div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
									<button 
										className="button button-blue" 
										onClick={handleSave}
										disabled={saving}
									>
										{saving ? 'Saving...' : 'Save'}
									</button>
								</div>
							)}

							{/* Project Recalculation Section */}
							<div className="form-row col-full row-label" style={{marginTop: '40px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
								<h3 style={{margin: 0, fontSize: '18px', color: '#333'}}>Project Recalculation</h3>
							</div>
							
							<div className="d-flex flex-row flex-wrap" style={{margin: '0 -10px'}}>
								<div className="form-row col-full" style={{padding: '0 10px', marginBottom: '20px'}}>
									<p style={{color: '#666', fontSize: '14px', marginBottom: '15px'}}>
										Recalculates billed hours, unbilled hours, earliest deadline, retainer and balance for all projects. Use this after system updates or if numbers look wrong.
									</p>
									
									{lastRecalculation && (
										<div className="alert alert-info" style={{marginBottom: '15px', padding: '12px', backgroundColor: '#e8f4f8', border: '1px solid #b8e0e8', borderRadius: '4px', fontSize: '13px'}}>
											<strong>Last run:</strong> {new Date(lastRecalculation.triggered_at).toLocaleString()}<br/>
											<strong>By:</strong> {lastRecalculation.triggered_by}<br/>
											<strong>Results:</strong> {lastRecalculation.results.success} successful, {lastRecalculation.results.errors.length} errors
										</div>
									)}
									
									<button 
										type="button"
										className="button button-blue"
										onClick={handleRecalculateFinancials}
										disabled={isRecalculating}
									>
										{isRecalculating ? 'Recalculating...' : 'Recalculate All Projects'}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
